import type { ProductsData, ProductsPayload } from '@/app/api/search/products/createProductsData.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import { type SearchInfoData, type SearchInfoDetailData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getSearchHash } from '@/app/components/search/Utilities.ts';
import type { CuriosityParams } from '../Curiosity';
import type { Localization } from './Localization';
import getSearchParams from './getSearchParams';
import { APIStatusEnum } from './reloadData/APIStatusEnum';
import clientAPICall from './clientAPICall.ts';

export type HandleWaitingFinishedParams = {
  isNewSearch: boolean;
  searchParams: SearchParams;
  productsData?: ProductsData;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  commonDataToUse: CommonData;
  localizationToUse: Localization;
  curiosityParamsToUse: CuriosityParams;
};
export type HandleWaitingFinished = (params: HandleWaitingFinishedParams) => void;

type WaitingCallProps = {
  searchParamsInitial: SearchParams;
  commonData: CommonData;
  localization: Localization;
  count?: number;
  handleWaitingFinished: HandleWaitingFinished;
  setAPIStatus: (apiStatus: APIStatusEnum) => void;
  curiosityParams: CuriosityParams;
  readyCall?: boolean;
  isNewSearch: boolean;
};

const waitingCallMapProducts = (payload: ProductsPayload, commonData: CommonData, searchInfoData: SearchInfoData) =>
  clientAPICall<ProductsPayload, ProductsData>({
    apiName: API_NAME.SEARCH_PROGRESS,
    payload,
    traceParent: searchInfoData.searchTraceParent,
    commonData,
  });

const readyCallMapProducts = (payload: ProductsPayload, commonData: CommonData, searchInfoData: SearchInfoData) =>
  clientAPICall<ProductsPayload, ProductsData>({
    apiName: API_NAME.PRODUCTS,
    payload,
    traceParent: searchInfoData.searchTraceParent,
    commonData,
  });

export const waitingCall = ({
  searchParamsInitial,
  commonData,
  localization,
  count = 0,
  handleWaitingFinished,
  setAPIStatus,
  curiosityParams,
  readyCall = false,
  isNewSearch,
}: WaitingCallProps) => {
  const { searchInfoData, searchInfoDetailData } = commonData;
  const searchParams = getSearchParams() as SearchParams;
  const searchHashInitial = getSearchHash(searchParamsInitial);
  const searchHash = getSearchHash(searchParams);
  // If a new search has started (different search criteria) then cancel any existing polling
  if (searchHash !== searchHashInitial) return;

  if (count === 60) {
    setAPIStatus(APIStatusEnum.READY);
    handleWaitingFinished({
      isNewSearch,
      searchParams,
      searchInfoData,
      searchInfoDetailData,
      commonDataToUse: commonData,
      localizationToUse: localization,
      curiosityParamsToUse: curiosityParams,
    });
  } else {
    setTimeout(() => {
      const payload: ProductsPayload = {
        searchParams,
        isSearchFromServer: false,
        commonData,
        localization,
        curiosityParams,
      };
      const mapProductsCall = readyCall ? readyCallMapProducts : waitingCallMapProducts;
      mapProductsCall(payload, commonData, searchInfoData)
        .then((response) => {
          if (response) {
            if (response.apiStatus === APIStatusEnum.WAIT) {
              // eslint-disable-next-line no-param-reassign
              count += 1;
              waitingCall({
                searchParamsInitial,
                commonData,
                localization,
                count,
                handleWaitingFinished,
                setAPIStatus,
                curiosityParams,
                isNewSearch,
              });
            } else if (readyCall && (response as ProductsData).apiStatus !== APIStatusEnum.WAIT) {
              setAPIStatus(response.apiStatus);
              handleWaitingFinished({
                isNewSearch,
                searchParams,
                productsData: response as ProductsData,
                searchInfoData,
                searchInfoDetailData,
                commonDataToUse: commonData,
                localizationToUse: localization,
                curiosityParamsToUse: curiosityParams,
              });
            } else {
              waitingCall({
                searchParamsInitial,
                commonData,
                localization,
                count,
                handleWaitingFinished,
                setAPIStatus,
                curiosityParams,
                readyCall: true,
                isNewSearch,
              });
            }
          } else {
            // eslint-disable-next-line no-param-reassign
            count += 1;
            waitingCall({
              searchParamsInitial,
              commonData,
              localization,
              count,
              handleWaitingFinished,
              setAPIStatus,
              curiosityParams,
              readyCall,
              isNewSearch,
            });
          }
        })
        .catch((error) => {
          console.log(error);
          // eslint-disable-next-line no-param-reassign
          count += 1;
          waitingCall({
            searchParamsInitial,
            commonData,
            localization,
            count,
            handleWaitingFinished,
            setAPIStatus,
            curiosityParams,
            isNewSearch,
          });
        });
    }, 1000);
  }
};
